import type { CSSProperties } from "react";
import type { NodeProps } from "@xyflow/react";
import {
  GraphNodeDepthTier,
  GraphNodeShapeKind,
  getDepthBoxShadow,
  getSelectedGlow,
  type GraphNodeShapeDescriptor,
} from "../Cmarkdowngraphcontrols";
import {
  DEPTH_COLORS,
  DIMMED_OPACITY,
  HEXAGON_CLIP_PATH,
  NODE_FILL,
  TRANSITION_DURATION,
} from "./mindmap-constants";
import {
  MindmapNodeShape,
  type MindmapFlowNode,
  type MindmapNodeData,
} from "./mindmap-types";
import { FontWeight } from "@/theme/typography";

const ROOT_BACKGROUND =
  "radial-gradient(circle at 30% 30%, rgba(102, 254, 193, 0.3), rgba(0, 55, 29, 0.7) 85%)";

const MINDMAP_ROUNDED_RADIUS = 59;
const MINDMAP_ROOT_BORDER_WIDTH = 2;
const MINDMAP_CHILD_BORDER_WIDTH = 1.5;
const MINDMAP_HOVER_SCALE = 1.02;
const MINDMAP_REST_SCALE = 1;
const MINDMAP_ENTRANCE_SCALE = "scale(0.95) translateX(-8px)";
const MINDMAP_ENTRANCE_DEPTH_DELAY_MS = 80;
const MINDMAP_ROOT_LABEL_SIZE = 15;
const MINDMAP_CHILD_LABEL_SIZE = 13;
const MINDMAP_ROOT_LINE_HEIGHT = "22px";
const MINDMAP_CHILD_LINE_HEIGHT = "18px";
const MINDMAP_ROOT_PADDING = "16px 28px";
const MINDMAP_CHILD_PADDING = "0 14px";
const MINDMAP_LABEL_TEXT_ALIGN_CENTER = "center";
const CURSOR_POINTER = "pointer";
const CURSOR_DEFAULT = "default";
const BORDER_ALPHA_WITH_CHILDREN = "B3";
const BORDER_ALPHA_WITHOUT_CHILDREN = "60";
const MINDMAP_HANDLE_TARGET = "target";
const MINDMAP_HANDLE_SOURCE = "source";

type MindmapNodePosition = NodeProps<MindmapFlowNode>["sourcePosition"];
type MindmapNodeHandlePosition = NonNullable<MindmapNodePosition>;

interface MindmapNodeModelInput {
  data: MindmapNodeData;
  sourcePosition: MindmapNodePosition;
  targetPosition: MindmapNodePosition;
  selectedNodeId: string | null;
  hoveredNodeId: string | null;
  highlightedNodeIds: ReadonlySet<string>;
  didEntranceStart: boolean;
  isHovered: boolean;
  isEntranceComplete: boolean;
}

interface MindmapNodeFrameModel {
  shape: GraphNodeShapeDescriptor;
  accentColor: string;
  fill: string;
  borderWidth: number;
  isSelected: boolean;
  boxShadow: string | undefined;
  opacity: number;
  transform: string;
  transition: string;
  cursor: CSSProperties["cursor"];
  contentPadding: CSSProperties["padding"];
}

export interface MindmapNodeHandleModel {
  type: typeof MINDMAP_HANDLE_TARGET | typeof MINDMAP_HANDLE_SOURCE;
  position: MindmapNodeHandlePosition;
}

interface MindmapNodeLabelModel {
  size: number;
  weight: FontWeight;
  textAlign: typeof MINDMAP_LABEL_TEXT_ALIGN_CENTER;
  lineHeight: CSSProperties["lineHeight"];
}

interface MindmapNodeModel {
  hasChildren: boolean;
  isCollapsed: boolean;
  isHexagon: boolean;
  frame: MindmapNodeFrameModel;
  connectionHandles: MindmapNodeHandleModel[];
  label: MindmapNodeLabelModel;
}

export function getMindmapNodeModel(input: MindmapNodeModelInput): MindmapNodeModel {
  const { data } = input;
  const hasChildren = hasNodeChildren(data);
  const isSelected = input.selectedNodeId === data.nodeId;
  const isActive = getIsNodeActive({
    hoveredNodeId: input.hoveredNodeId,
    highlightedNodeIds: input.highlightedNodeIds,
    nodeId: data.nodeId,
    isSelected,
  });
  const depthColor = getDepthColor(data.colorIndex);
  const depthTier = getDepthTier({ isRoot: data.isRoot, depth: data.depth });
  const isHexagon = getIsHexagon(data);

  return {
    hasChildren,
    isCollapsed: getIsCollapsed({ hasChildren, isExpanded: data.isExpanded }),
    isHexagon,
    frame: getFrameModel({
      data,
      hasChildren,
      isActive,
      isSelected,
      isHexagon,
      depthColor,
      depthTier,
      didEntranceStart: input.didEntranceStart,
      isHovered: input.isHovered,
      isEntranceComplete: input.isEntranceComplete,
    }),
    connectionHandles: getConnectionHandles({
      hasChildren,
      isHexagon,
      isRoot: data.isRoot,
      sourcePosition: input.sourcePosition,
      targetPosition: input.targetPosition,
    }),
    label: getLabelModel(data.isRoot),
  };
}

function hasNodeChildren(data: MindmapNodeData): boolean {
  return data.childCount > 0;
}

function getIsCollapsed(input: { hasChildren: boolean; isExpanded: boolean }): boolean {
  return input.hasChildren && !input.isExpanded;
}

function getIsNodeActive(input: {
  hoveredNodeId: string | null;
  highlightedNodeIds: ReadonlySet<string>;
  nodeId: string;
  isSelected: boolean;
}): boolean {
  return (
    input.hoveredNodeId == null ||
    input.highlightedNodeIds.has(input.nodeId) ||
    input.isSelected
  );
}

function getDepthColor(colorIndex: number): string {
  return DEPTH_COLORS[colorIndex % DEPTH_COLORS.length] ?? DEPTH_COLORS[0];
}

function getDepthTier(input: { isRoot: boolean; depth: number }): GraphNodeDepthTier {
  if (input.isRoot) {
    return GraphNodeDepthTier.Root;
  }

  return input.depth <= 1 ? GraphNodeDepthTier.Secondary : GraphNodeDepthTier.Further;
}

function getIsHexagon(data: MindmapNodeData): boolean {
  return !data.isRoot && data.shape === MindmapNodeShape.Hexagon;
}

function getFrameModel(input: {
  data: MindmapNodeData;
  hasChildren: boolean;
  isActive: boolean;
  isSelected: boolean;
  isHexagon: boolean;
  depthColor: string;
  depthTier: GraphNodeDepthTier;
  didEntranceStart: boolean;
  isHovered: boolean;
  isEntranceComplete: boolean;
}): MindmapNodeFrameModel {
  const selectedGlowSuffix = getSelectedGlowSuffix({
    isSelected: input.isSelected,
    depthTier: input.depthTier,
  });

  return {
    shape: getShape(input.isHexagon),
    accentColor: getBorderColor({
      hasChildren: input.hasChildren,
      depthColor: input.depthColor,
    }),
    fill: getFrameFill({
      isHexagon: input.isHexagon,
      isRoot: input.data.isRoot,
    }),
    borderWidth: getBorderWidth(input.data.isRoot),
    isSelected: input.isSelected,
    boxShadow: getBoxShadow({
      data: input.data,
      isHexagon: input.isHexagon,
      isSelected: input.isSelected,
      depthColor: input.depthColor,
      depthTier: input.depthTier,
      selectedGlowSuffix,
    }),
    opacity: getOpacity({
      didEntranceStart: input.didEntranceStart,
      isActive: input.isActive,
    }),
    transform: getTransform({
      didEntranceStart: input.didEntranceStart,
      hasChildren: input.hasChildren,
      isHovered: input.isHovered,
    }),
    transition: getTransition({
      isEntranceComplete: input.isEntranceComplete,
      entranceDelayMs: input.data.depth * MINDMAP_ENTRANCE_DEPTH_DELAY_MS,
    }),
    cursor: getCursor(input.hasChildren),
    contentPadding: getContentPadding({
      isHexagon: input.isHexagon,
      isRoot: input.data.isRoot,
    }),
  };
}

function getFrameFill(input: { isHexagon: boolean; isRoot: boolean }): string {
  if (input.isHexagon) {
    return NODE_FILL;
  }

  return input.isRoot ? ROOT_BACKGROUND : NODE_FILL;
}

function getBorderWidth(isRoot: boolean): number {
  return isRoot ? MINDMAP_ROOT_BORDER_WIDTH : MINDMAP_CHILD_BORDER_WIDTH;
}

function getCursor(hasChildren: boolean): CSSProperties["cursor"] {
  return hasChildren ? CURSOR_POINTER : CURSOR_DEFAULT;
}

function getContentPadding(input: {
  isHexagon: boolean;
  isRoot: boolean;
}): CSSProperties["padding"] {
  if (input.isHexagon) {
    return MINDMAP_CHILD_PADDING;
  }

  return getNodePadding(input.isRoot);
}

function getBorderColor(input: { hasChildren: boolean; depthColor: string }): string {
  const alpha = input.hasChildren
    ? BORDER_ALPHA_WITH_CHILDREN
    : BORDER_ALPHA_WITHOUT_CHILDREN;
  return `${input.depthColor}${alpha}`;
}

function getSelectedGlowSuffix(input: {
  isSelected: boolean;
  depthTier: GraphNodeDepthTier;
}): string {
  return input.isSelected ? `, ${getSelectedGlow(input.depthTier)}` : "";
}

function getShape(isHexagon: boolean): GraphNodeShapeDescriptor {
  if (isHexagon) {
    return { kind: GraphNodeShapeKind.ClipPath, clipPath: HEXAGON_CLIP_PATH };
  }

  return {
    kind: GraphNodeShapeKind.BorderRadius,
    borderRadius: MINDMAP_ROUNDED_RADIUS,
  };
}

function getBoxShadow(input: {
  data: MindmapNodeData;
  isHexagon: boolean;
  isSelected: boolean;
  depthColor: string;
  depthTier: GraphNodeDepthTier;
  selectedGlowSuffix: string;
}): string | undefined {
  if (input.isHexagon) {
    return getHexagonBoxShadow(input);
  }

  return `${getDepthBoxShadow(input.depthTier)}${getCloudExtraShadow(input)}${input.selectedGlowSuffix}`;
}

function getCloudExtraShadow(input: {
  data: MindmapNodeData;
  depthColor: string;
}): string {
  const isCloud = !input.data.isRoot && input.data.shape === MindmapNodeShape.Cloud;
  return isCloud ? `, 0 12px 24px -20px ${input.depthColor}` : "";
}

function getHexagonBoxShadow(input: {
  data: MindmapNodeData;
  isSelected: boolean;
  depthColor: string;
  depthTier: GraphNodeDepthTier;
  selectedGlowSuffix: string;
}): string | undefined {
  const isCloud = !input.data.isRoot && input.data.shape === MindmapNodeShape.Cloud;
  if (isCloud) {
    return `0 12px 24px -20px ${input.depthColor}${input.selectedGlowSuffix}`;
  }

  return input.isSelected ? getSelectedGlow(input.depthTier) : undefined;
}

function getOpacity(input: { didEntranceStart: boolean; isActive: boolean }): number {
  if (!input.didEntranceStart) {
    return 0;
  }

  return input.isActive ? 1 : DIMMED_OPACITY;
}

function getTransform(input: {
  didEntranceStart: boolean;
  hasChildren: boolean;
  isHovered: boolean;
}): string {
  if (!input.didEntranceStart) {
    return MINDMAP_ENTRANCE_SCALE;
  }

  const hoverScale = input.hasChildren && input.isHovered
    ? MINDMAP_HOVER_SCALE
    : MINDMAP_REST_SCALE;
  return `scale(${hoverScale})`;
}

function getTransition(input: {
  isEntranceComplete: boolean;
  entranceDelayMs: number;
}): string {
  if (input.isEntranceComplete) {
    return [
      `transform ${TRANSITION_DURATION} ease-out`,
      `border-color ${TRANSITION_DURATION} ease-out`,
      `opacity ${TRANSITION_DURATION} ease-out`,
      `box-shadow ${TRANSITION_DURATION} ease-out`,
      `outline-color ${TRANSITION_DURATION} ease-out`,
    ].join(", ");
  }

  return [
    `opacity 400ms ease-out ${input.entranceDelayMs}ms`,
    `transform 400ms ease-out ${input.entranceDelayMs}ms`,
    `border-color ${TRANSITION_DURATION} ease-out`,
    `box-shadow ${TRANSITION_DURATION} ease-out`,
    `outline-color ${TRANSITION_DURATION} ease-out`,
  ].join(", ");
}

function getNodePadding(isRoot: boolean): CSSProperties["padding"] {
  return isRoot ? MINDMAP_ROOT_PADDING : MINDMAP_CHILD_PADDING;
}

function getLabelModel(isRoot: boolean): MindmapNodeLabelModel {
  return {
    size: isRoot ? MINDMAP_ROOT_LABEL_SIZE : MINDMAP_CHILD_LABEL_SIZE,
    weight: isRoot ? FontWeight.Bold : FontWeight.Medium,
    textAlign: MINDMAP_LABEL_TEXT_ALIGN_CENTER,
    lineHeight: isRoot ? MINDMAP_ROOT_LINE_HEIGHT : MINDMAP_CHILD_LINE_HEIGHT,
  };
}

function getConnectionHandles(input: {
  hasChildren: boolean;
  isHexagon: boolean;
  isRoot: boolean;
  sourcePosition: MindmapNodePosition;
  targetPosition: MindmapNodePosition;
}): MindmapNodeHandleModel[] {
  return [
    getTargetHandleModel(input),
    getSourceHandleModel({
      hasChildren: input.hasChildren,
      sourcePosition: input.sourcePosition,
    }),
  ].filter(isMindmapNodeHandleModel);
}

function getTargetHandleModel(input: {
  isHexagon: boolean;
  isRoot: boolean;
  targetPosition: MindmapNodePosition;
}): MindmapNodeHandleModel | null {
  const canRenderTarget = input.isHexagon || !input.isRoot;
  if (!canRenderTarget || !input.targetPosition) {
    return null;
  }

  return {
    type: MINDMAP_HANDLE_TARGET,
    position: input.targetPosition,
  };
}

function getSourceHandleModel(input: {
  hasChildren: boolean;
  sourcePosition: MindmapNodePosition;
}): MindmapNodeHandleModel | null {
  if (!input.hasChildren || !input.sourcePosition) {
    return null;
  }

  return {
    type: MINDMAP_HANDLE_SOURCE,
    position: input.sourcePosition,
  };
}

function isMindmapNodeHandleModel(
  handle: MindmapNodeHandleModel | null,
): handle is MindmapNodeHandleModel {
  return handle != null;
}
